import React, { useEffect, useRef, useState } from 'react';
import { collection, collectionGroup, doc, getDoc, getDocs, limit, onSnapshot, query } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../services/firebase';
import type { Artwork, User } from '../types';
import { useFeedCache } from './FeedCacheContext';

/**
 * FeedPrewarmProvider
 * - Starts a cache-first Firestore listener on app startup
 * - Immediately yields cached docs (from IndexedDB) so UI can render fast
 * - Hydrates with server updates seamlessly in the background
 * - Maps posts to `Artwork` with minimal user profile lookups (cached per session)
 */
export const FeedPrewarmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setPosts, setLoading, setReposts } = useFeedCache();
  const userCacheRef = useRef<Record<string, User>>({});
  const unsubRef = useRef<(() => void) | null>(null);
  const repostsUnsubRef = useRef<(() => void) | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    // Wait for auth state to be determined before starting listeners
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      console.log('[FeedPrewarmProvider] Auth state changed:', user ? 'authenticated' : 'not authenticated');
      setAuthInitialized(true);
      
      // Don't start listeners if user is not authenticated (avoids permission errors)
      if (!user) {
        console.log('[FeedPrewarmProvider] No authenticated user, skipping posts fetch');
        setLoading(false);
        return;
      }

      console.log('[FeedPrewarmProvider] Starting posts fetch for user:', user.uid);

    const q = query(collection(db, 'posts'), limit(100)); // No orderBy to avoid index requirement, sort client-side
    setLoading(true);

    // Cleanup any existing subscription
    if (unsubRef.current) {
      try { unsubRef.current(); } catch {}
      unsubRef.current = null;
    }
    if (repostsUnsubRef.current) {
      try { repostsUnsubRef.current(); } catch {}
      repostsUnsubRef.current = null;
    }

    // Fallback one-time fetch function (defined before use)
    const fetchPostsOnce = async () => {
      try {
        console.log('[FeedPrewarmProvider] Trying one-time fetch...');
        const snap = await getDocs(query(collection(db, 'posts'), limit(100)));
        console.log('[FeedPrewarmProvider] One-time fetch count:', snap.docs.length);
        
        const mapped: Artwork[] = snap.docs.map((docSnap) => {
          const d = docSnap.data() as any;
          const createdAt = (d.createdAt && typeof d.createdAt.toDate === 'function')
            ? d.createdAt.toDate().toISOString()
            : (typeof d.createdAt === 'string' ? d.createdAt : new Date().toISOString());
          return {
            id: docSnap.id,
            userId: d.userId,
            user: {
              id: d.userId || 'unknown',
              username: 'user',
              displayName: 'User',
              avatar: '/crevvi-logo.png',
              followers: 0,
              following: 0,
              verified: false,
              specialties: [],
            } as User,
            title: d.title || 'Untitled',
            description: d.description || '',
            imageUrl: (d.imageUrl as string) || '',
            videoUrl: typeof d.videoUrl === 'string' ? d.videoUrl : undefined,
            mediaType: typeof d.mediaType === 'string' ? (d.mediaType === 'video' ? 'video' : 'image') : (d.videoUrl ? 'video' : undefined),
            tags: Array.isArray(d.tags) ? d.tags : [],
            likes: typeof d.likes === 'number' ? d.likes : 0,
            comments: typeof d.comments === 'number' ? d.comments : 0,
            shares: typeof d.shares === 'number' ? d.shares : 0,
            createdAt,
            isLiked: false,
            category: d.category || 'general',
            isPinned: !!d.isPinned,
            isCommentingDisabled: !!d.isCommentingDisabled,
            archived: !!d.archived,
          } as Artwork;
        });

        // Sort by createdAt descending
        const sorted = [...mapped].sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        console.log('[FeedPrewarmProvider] Fallback setting posts:', sorted.length);
        setPosts(sorted);
      } catch (fallbackErr) {
        console.error('[FeedPrewarmProvider] Fallback fetch failed:', fallbackErr);
      }
    };

    try {
      unsubRef.current = onSnapshot(
        q,
        { includeMetadataChanges: true },
        async (snap) => {
          const docs = snap.docs;
          console.log('[FeedPrewarmProvider] Fetched posts count:', docs.length);
          
          // Guard: if cache-only snapshot arrives empty, don't wipe the feed
          try {
            const meta: any = (snap as any).metadata || {};
            const fromCache = !!meta.fromCache;
            const hasPendingWrites = !!meta.hasPendingWrites;
            console.log('[FeedPrewarmProvider] Snapshot metadata - fromCache:', fromCache, 'hasPendingWrites:', hasPendingWrites);
            if (docs.length === 0 && fromCache && !hasPendingWrites) {
              console.log('[FeedPrewarmProvider] Empty cache snapshot, keeping existing posts');
              setLoading(false);
              return;
            }
          } catch {}
          const uids = Array.from(new Set(docs.map(d => (d.data() as any).userId).filter(Boolean)));
          const cache = userCacheRef.current;
          const missing = uids.filter(uid => !cache[uid]);

          // Fetch any missing user profiles
          await Promise.all(missing.map(async (uid) => {
            try {
              const s = await getDoc(doc(db, 'users', uid));
              const d = s.data() || {};
              cache[uid] = {
                id: uid,
                username: (d.username as string) || 'user',
                displayName: (d.displayName as string) || 'User',
                avatar: (d.avatar as string) || '',
                followers: (d.followers as number) || 0,
                following: (d.following as number) || 0,
                verified: (d.verified as boolean) || false,
                specialties: Array.isArray(d.specialties) ? d.specialties as string[] : [],
              } as User;
            } catch {
              cache[uid] = {
                id: uid,
                username: 'user',
                displayName: 'User',
                avatar: '',
                followers: 0,
                following: 0,
                verified: false,
                specialties: [],
              } as User;
            }
          }));

          const mapped: Artwork[] = docs.map((docSnap) => {
            const d = docSnap.data() as any;
            const createdAt = (d.createdAt && typeof d.createdAt.toDate === 'function')
              ? d.createdAt.toDate().toISOString()
              : (typeof d.createdAt === 'string' ? d.createdAt : new Date().toISOString());
            const user: User = cache[d.userId] || {
              id: d.userId || 'unknown',
              username: 'user',
              displayName: 'User',
              avatar: '/crevvi-logo.png',
              followers: 0,
              following: 0,
              verified: false,
              specialties: [],
            };
            return {
              id: docSnap.id,
              userId: d.userId,
              user,
              title: d.title || 'Untitled',
              description: d.description || '',
              imageUrl: (d.imageUrl as string) || '',
              videoUrl: typeof d.videoUrl === 'string' ? d.videoUrl : undefined,
              mediaType: typeof d.mediaType === 'string' ? (d.mediaType === 'video' ? 'video' : 'image') : (d.videoUrl ? 'video' : undefined),
              tags: Array.isArray(d.tags) ? d.tags : [],
              likes: typeof d.likes === 'number' ? d.likes : 0,
              comments: typeof d.comments === 'number' ? d.comments : 0,
              shares: typeof d.shares === 'number' ? d.shares : 0,
              createdAt,
              isLiked: false,
              category: d.category || 'general',
              isPinned: !!d.isPinned,
              isCommentingDisabled: !!d.isCommentingDisabled,
              archived: !!d.archived,
            } as Artwork;
          });

          // Sort by createdAt descending (newest first)
          const sorted = [...mapped].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );

          console.log('[FeedPrewarmProvider] Setting posts:', sorted.length);
          setPosts(sorted);
          setLoading(false);
        },
        (err: any) => {
          // Log all errors to help diagnose issues
          console.error('[FeedPrewarmProvider] posts onSnapshot error:', err?.code, err?.message);
          if (err?.code !== 'permission-denied') {
            console.warn('[FeedPrewarmProvider] posts error details:', err);
          }
          // Fallback: try one-time fetch
          fetchPostsOnce();
          setLoading(false);
        }
      );
    } catch (e) {
      console.warn('[FeedPrewarmProvider] failed to start snapshot:', e);
      // Fallback: try one-time fetch
      fetchPostsOnce();
      setLoading(false);
    }

    // Start a lightweight listener for recent reposts to populate separate section
    try {
      // Avoid requiring a single-field index on reposts.createdAt by not ordering here
      const rq = query(collectionGroup(db, 'reposts'), limit(100));
      repostsUnsubRef.current = onSnapshot(
        rq,
        { includeMetadataChanges: true },
        (snap) => {
          const cache = userCacheRef.current;
          // Build a map from postId to repost meta entries (multiple users may repost same post)
          (async () => {
            const entries = await Promise.all(snap.docs.map(async (d: any) => {
              try {
                const parent = d.ref?.parent?.parent;
                const postId: string | null = parent?.id || null;
                if (!postId) return null;
                const data: any = d.data() || {};
                const reposterId: string = data.userId || d.id;
                if (reposterId && !cache[reposterId]) {
                  try {
                    const s = await getDoc(doc(db, 'users', reposterId));
                    const u = s.data() || {};
                    cache[reposterId] = {
                      id: reposterId,
                      username: (u.username as string) || 'user',
                      displayName: (u.displayName as string) || 'User',
                      avatar: (u.avatar as string) || '',
                      followers: (u.followers as number) || 0,
                      following: (u.following as number) || 0,
                      verified: (u.verified as boolean) || false,
                      specialties: Array.isArray(u.specialties) ? (u.specialties as string[]) : [],
                    } as User;
                  } catch {}
                }
                const postSnap = await getDoc(doc(db, 'posts', postId));
                const p = postSnap.data() as any;
                if (!p) return null;
                const postCreatedAt = (p.createdAt && typeof p.createdAt.toDate === 'function')
                  ? p.createdAt.toDate().toISOString()
                  : (typeof p.createdAt === 'string' ? p.createdAt : new Date().toISOString());
                const owner: User = cache[p.userId] || {
                  id: p.userId || 'unknown',
                  username: 'user',
                  displayName: 'User',
                  avatar: '/crevvi-logo.png',
                  followers: 0,
                  following: 0,
                  verified: false,
                  specialties: [],
                };
                const reposter = cache[reposterId] || owner;
                const rts = data.createdAt;
                let repostCreatedAt: string | null = null;
                if (rts && typeof rts.toDate === 'function') repostCreatedAt = rts.toDate().toISOString();
                else if (typeof rts === 'string' || typeof rts === 'number') repostCreatedAt = new Date(rts).toISOString();

                const art: Artwork = {
                  id: postId,
                  userId: p.userId,
                  user: owner,
                  title: p.title || 'Untitled',
                  description: p.description || '',
                  imageUrl: (p.imageUrl as string) || '',
                  videoUrl: typeof p.videoUrl === 'string' ? p.videoUrl : undefined,
                  mediaType: typeof p.mediaType === 'string' ? (p.mediaType === 'video' ? 'video' : 'image') : (p.videoUrl ? 'video' : undefined),
                  tags: Array.isArray(p.tags) ? p.tags : [],
                  likes: typeof p.likes === 'number' ? p.likes : 0,
                  comments: typeof p.comments === 'number' ? p.comments : 0,
                  shares: typeof p.shares === 'number' ? p.shares : 0,
                  reposts: typeof p.reposts === 'number' ? p.reposts : undefined,
                  createdAt: postCreatedAt,
                  isLiked: false,
                  category: p.category || 'general',
                  isPinned: !!p.isPinned,
                  isCommentingDisabled: !!p.isCommentingDisabled,
                  archived: !!p.archived,
                  isRepost: true,
                  repostByUser: reposter,
                  repostCaption: typeof data.caption === 'string' ? data.caption : null,
                  repostCreatedAt,
                  originalPostId: postId,
                };
                return art;
              } catch {
                return null;
              }
            }));

            const items = entries.filter(Boolean) as Artwork[];
            setReposts(items);
          })();
        },
        (err: any) => {
          // Silently ignore permission errors
          if (err?.code !== 'permission-denied') {
            console.warn('[FeedPrewarmProvider] reposts onSnapshot error:', err);
          }
        }
      );
    } catch (e) {
      console.warn('[FeedPrewarmProvider] failed to start reposts snapshot:', e);
    }

    }); // Close onAuthStateChanged callback

    return () => {
      unsubscribeAuth();
      try { unsubRef.current?.(); } catch {}
      unsubRef.current = null;
      try { repostsUnsubRef.current?.(); } catch {}
      repostsUnsubRef.current = null;
    };
  }, [setPosts, setLoading, setReposts]);

  // no-op wrapper
  return <>{children}</>;
};
